import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Settings, Facebook, Instagram, Globe, Mail, MessageSquare,
  Link2, CheckCircle2, XCircle, ExternalLink, Copy, Code,
  Webhook, Zap, RefreshCw, ChevronRight, AlertCircle, Loader2,
  PlusCircle, Trash2, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import CRMLayout from '../../components/crm/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const CRMIntegrations = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState({});
  const [webhookUrl, setWebhookUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [activeTab, setActiveTab] = useState('sources');
  const [connectingPlatform, setConnectingPlatform] = useState(null);

  // Available lead sources
  const leadSources = [
    {
      id: 'facebook',
      name: 'Facebook Lead Ads',
      icon: Facebook,
      color: '#1877F2',
      description: 'Import leads from your Facebook ad campaigns automatically',
      status: integrations.facebook?.connected ? 'connected' : 'disconnected',
      features: ['Auto-sync leads', 'Custom field mapping', 'Real-time updates']
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: '#E4405F',
      description: 'Capture leads from Instagram DMs and contact forms',
      status: integrations.instagram?.connected ? 'connected' : 'disconnected',
      features: ['DM lead capture', 'Bio link forms', 'Story responses']
    },
    {
      id: 'website',
      name: 'Website Forms',
      icon: Globe,
      color: '#10B981',
      description: 'Embed lead capture forms on your website',
      status: integrations.website?.connected ? 'connected' : 'disconnected',
      features: ['Embeddable widget', 'Custom styling', 'Multi-step forms']
    },
    {
      id: 'email',
      name: 'Email Integration',
      icon: Mail,
      color: '#F59E0B',
      description: 'Sync leads from email inquiries and newsletters',
      status: integrations.email?.connected ? 'connected' : 'disconnected',
      features: ['Email parsing', 'Auto-create leads', 'Thread tracking']
    },
    {
      id: 'livechat',
      name: 'Live Chat',
      icon: MessageSquare,
      color: '#8B5CF6',
      description: 'Convert chat conversations into leads',
      status: integrations.livechat?.connected ? 'connected' : 'disconnected',
      features: ['Chat transcripts', 'Visitor info capture', 'Bot integration']
    },
    {
      id: 'webhook',
      name: 'Webhook / API',
      icon: Webhook,
      color: '#EC4899',
      description: 'Receive leads from any external source via webhook',
      status: integrations.webhook?.connected ? 'connected' : 'disconnected',
      features: ['Custom webhooks', 'REST API', 'Zapier compatible']
    }
  ];

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.get(`${API}/api/crm/integrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrations(response.data.integrations || {});
      setWebhookUrl(response.data.webhook_url || '');
      setApiKey(response.data.api_key || '');
    } catch (error) {
      console.error('Error fetching integrations:', error);
      // Generate default webhook URL and API key for display
      const companyId = localStorage.getItem('crm_company_id') || 'demo';
      setWebhookUrl(`${API}/api/crm/webhook/leads/${companyId}`);
      setApiKey(`crm_${companyId}_${Date.now()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (platformId) => {
    setConnectingPlatform(platformId);
    
    try {
      if (platformId === 'facebook' || platformId === 'instagram') {
        // For Facebook/Instagram, we need OAuth flow
        toast.info('Redirecting to Meta for authentication...');
        // In production, this would redirect to Meta OAuth
        // For now, simulate the connection
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        setIntegrations(prev => ({
          ...prev,
          [platformId]: { connected: true, connectedAt: new Date().toISOString() }
        }));
        toast.success(`${platformId === 'facebook' ? 'Facebook' : 'Instagram'} connected successfully!`);
      } else if (platformId === 'website') {
        // Show website widget setup
        setActiveTab('widget');
        toast.success('Website integration ready! Copy the widget code below.');
        setIntegrations(prev => ({
          ...prev,
          website: { connected: true, connectedAt: new Date().toISOString() }
        }));
      } else if (platformId === 'webhook') {
        // Enable webhook
        setActiveTab('api');
        toast.success('Webhook enabled! Use the URL below to send leads.');
        setIntegrations(prev => ({
          ...prev,
          webhook: { connected: true, connectedAt: new Date().toISOString() }
        }));
      } else {
        // Generic connection
        await new Promise(resolve => setTimeout(resolve, 1500));
        setIntegrations(prev => ({
          ...prev,
          [platformId]: { connected: true, connectedAt: new Date().toISOString() }
        }));
        toast.success('Integration connected successfully!');
      }
    } catch (error) {
      toast.error('Failed to connect integration');
    } finally {
      setConnectingPlatform(null);
    }
  };

  const handleDisconnect = async (platformId) => {
    try {
      setIntegrations(prev => ({
        ...prev,
        [platformId]: { connected: false }
      }));
      toast.success('Integration disconnected');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const getWidgetCode = () => {
    const companyId = localStorage.getItem('crm_company_id') || 'demo';
    return `<!-- CLOUD IT CRM Lead Capture Widget -->
<script>
  (function(w,d,s,o,f,js,fjs){
    w['CloudITWidget']=o;w[o]=w[o]||function(){
    (w[o].q=w[o].q||[]).push(arguments)};
    js=d.createElement(s);fjs=d.getElementsByTagName(s)[0];
    js.id=o;js.src=f;js.async=1;fjs.parentNode.insertBefore(js,fjs);
  }(window,document,'script','cloudit','${API}/widget.js'));
  cloudit('init', '${companyId}');
</script>
<!-- End CLOUD IT CRM Widget -->`;
  };

  const getWebhookExample = () => {
    return `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "name": "John Doe",
    "email": "john@example.com",
    "phone": "+1234567890",
    "source": "website",
    "notes": "Interested in premium plan"
  }'`;
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </CRMLayout>
    );
  }

  return (
    <CRMLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Lead Sources & Integrations</h1>
            <p className="text-slate-400 mt-1">Connect your lead sources to automatically import leads</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-700 pb-2">
          {[
            { id: 'sources', label: 'Lead Sources', icon: Zap },
            { id: 'widget', label: 'Website Widget', icon: Code },
            { id: 'api', label: 'API & Webhooks', icon: Webhook }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === tab.id 
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Lead Sources Tab */}
        {activeTab === 'sources' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {leadSources.map(source => (
              <div 
                key={source.id}
                className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 hover:border-slate-600/50 transition-all"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ background: `${source.color}20` }}
                    >
                      <source.icon className="w-6 h-6" style={{ color: source.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{source.name}</h3>
                      <div className="flex items-center gap-1 mt-0.5">
                        {integrations[source.id]?.connected ? (
                          <>
                            <CheckCircle2 className="w-3 h-3 text-green-400" />
                            <span className="text-xs text-green-400">Connected</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3 text-slate-500" />
                            <span className="text-xs text-slate-500">Not connected</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-slate-400 mb-4">{source.description}</p>

                {/* Features */}
                <div className="space-y-2 mb-4">
                  {source.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-slate-500">
                      <ChevronRight className="w-3 h-3" />
                      {feature}
                    </div>
                  ))}
                </div>

                {/* Action Button */}
                {integrations[source.id]?.connected ? (
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleDisconnect(source.id)}
                      className="flex-1 py-2 px-3 bg-slate-700/50 hover:bg-slate-700 text-slate-300 text-sm rounded-lg transition-all"
                    >
                      Disconnect
                    </button>
                    <button 
                      className="py-2 px-3 bg-slate-700/50 hover:bg-slate-700 text-slate-300 rounded-lg transition-all"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => handleConnect(source.id)}
                    disabled={connectingPlatform === source.id}
                    className="w-full py-2 px-3 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {connectingPlatform === source.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <PlusCircle className="w-4 h-4" />
                        Connect
                      </>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Website Widget Tab */}
        {activeTab === 'widget' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-2">Embed Lead Capture Form</h2>
              <p className="text-slate-400 text-sm mb-6">
                Add this code to your website to display a lead capture form. Leads will automatically appear in your CRM.
              </p>

              {/* Code Block */}
              <div className="relative">
                <pre className="bg-slate-900 rounded-lg p-4 overflow-x-auto text-sm text-slate-300 border border-slate-700">
                  <code>{getWidgetCode()}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(getWidgetCode(), 'Widget code')}
                  className="absolute top-3 right-3 p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all"
                >
                  <Copy className="w-4 h-4 text-slate-300" />
                </button>
              </div>

              {/* Instructions */}
              <div className="mt-6 space-y-3">
                <h3 className="text-sm font-medium text-white">Installation Instructions:</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-slate-400">
                  <li>Copy the code above</li>
                  <li>Paste it just before the closing <code className="bg-slate-700 px-1 rounded">&lt;/body&gt;</code> tag on your website</li>
                  <li>The widget will automatically appear on your site</li>
                  <li>Customize the appearance in Widget Settings</li>
                </ol>
              </div>
            </div>

            {/* Widget Preview */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Widget Preview</h2>
              <div className="bg-white rounded-xl p-6 max-w-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Get in Touch</h3>
                <div className="space-y-3">
                  <input 
                    type="text" 
                    placeholder="Your Name" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900"
                    disabled
                  />
                  <input 
                    type="email" 
                    placeholder="Email Address" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900"
                    disabled
                  />
                  <input 
                    type="tel" 
                    placeholder="Phone Number" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900"
                    disabled
                  />
                  <textarea 
                    placeholder="How can we help?" 
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900"
                    disabled
                  />
                  <button className="w-full py-2 bg-blue-500 text-white rounded-lg font-medium">
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* API & Webhooks Tab */}
        {activeTab === 'api' && (
          <div className="space-y-6">
            {/* Webhook URL */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-2">Webhook URL</h2>
              <p className="text-slate-400 text-sm mb-4">
                Send lead data to this URL to create leads in your CRM automatically.
              </p>
              
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-3">
                  <code className="text-sm text-blue-400 break-all">{webhookUrl}</code>
                </div>
                <button
                  onClick={() => copyToClipboard(webhookUrl, 'Webhook URL')}
                  className="p-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all"
                >
                  <Copy className="w-5 h-5 text-slate-300" />
                </button>
              </div>
            </div>

            {/* API Key */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-2">API Key</h2>
              <p className="text-slate-400 text-sm mb-4">
                Include this key in the <code className="bg-slate-700 px-1 rounded">X-API-Key</code> header for authentication.
              </p>
              
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 flex items-center justify-between">
                  <code className="text-sm text-green-400">
                    {showApiKey ? apiKey : '••••••••••••••••••••••••'}
                  </code>
                  <button
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="p-1 hover:bg-slate-700 rounded transition-all"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                  </button>
                </div>
                <button
                  onClick={() => copyToClipboard(apiKey, 'API Key')}
                  className="p-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all"
                >
                  <Copy className="w-5 h-5 text-slate-300" />
                </button>
              </div>
            </div>

            {/* Example Request */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-2">Example Request</h2>
              <p className="text-slate-400 text-sm mb-4">
                Use this cURL command as a reference for sending leads via API.
              </p>
              
              <div className="relative">
                <pre className="bg-slate-900 rounded-lg p-4 overflow-x-auto text-sm text-slate-300 border border-slate-700">
                  <code>{getWebhookExample()}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(getWebhookExample(), 'Example code')}
                  className="absolute top-3 right-3 p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all"
                >
                  <Copy className="w-4 h-4 text-slate-300" />
                </button>
              </div>

              {/* Supported Fields */}
              <div className="mt-6">
                <h3 className="text-sm font-medium text-white mb-3">Supported Fields:</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {['name', 'email', 'phone', 'company', 'source', 'notes', 'value', 'tags'].map(field => (
                    <div key={field} className="bg-slate-900 px-3 py-2 rounded-lg">
                      <code className="text-xs text-slate-400">{field}</code>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Zapier Integration */}
            <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-orange-400" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-white mb-1">Zapier Integration</h2>
                  <p className="text-slate-400 text-sm mb-4">
                    Connect with 5,000+ apps using Zapier. Automatically import leads from Google Sheets, Typeform, Calendly, and more.
                  </p>
                  <button className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-all flex items-center gap-2">
                    Connect Zapier
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </CRMLayout>
  );
};

export default CRMIntegrations;
